import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { PersonsV3 } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import {
  AppointmentStatusComponents,
  AppointmentStatusHooks,
  AppointmentStatusTypes,
} from '@frontend/appointment-status';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InsuranceTypeProvider, ScheduleAppointmentCardInsuranceStatusChip } from '@frontend/insurance-tab';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useGetActivePetsQuery } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import { Text, styles as dsStyles, formatDate, useAlert } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../constants';
import { useGetSelectedCalendarDateAppointmentInfo } from '../../hooks/use-get-selected-calendar-date-appointment-info';
import { useCalendarStore } from '../../stores';
import { ExtendedAppointment } from '../../types';
import {
  OverlappingEventIndicator,
  OverlappingEventIndicatorVariantType,
} from '../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { EventCardComponentProps } from '../calendar-view/providers';
import { checkIsNonAttendedEventStatus } from '../calendar-view/utils';
import { AppointmentStatusV3Enum, WeaveAppointmentStatus } from '../v3/EventCardAppointmentStatusChip/types';
import { AppointmentEventCardPreview } from './appointment-event-card';
import { AppointmentStatusV2 } from './appointment-event-card/appointment-status-v2';
import { useAppointmentStatusShallowStore } from './appointment-event-card/appointmentStatusStore';
import { prepareAppointmentInfo } from './helpers';
import { MotionText } from './motion-text';

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const EventCardComponent = ({
  duration = '',
  id,
  providerId,
  title,
  eventType,
  overlappingIndicatorVariant,
  isYesterdayEvent,
}: EventCardComponentProps) => {
  const isAppointment = eventType === 'appointment';
  const appointmentDetails = useGetSelectedCalendarDateAppointmentInfo(id);

  if (isAppointment && !!appointmentDetails) {
    return (
      <AppointmentCardSwitcher
        duration={duration ?? '-'}
        appointmentDetails={appointmentDetails ?? {}}
        eventId={id}
        {...{ overlappingIndicatorVariant, isYesterdayEvent }}
      />
    );
  }

  // Break and Out of office Event Card collapsed view
  return (
    <div css={{ display: 'flex', paddingTop: theme.spacing(1) }}>
      <Icon name={providerId ? 'remove-small' : 'location-small'} color='light' css={{ minWidth: 'fit-content' }} />
      <div css={{ display: 'flex', flexDirection: 'column', marginLeft: theme.spacing(0.5), overflow: 'hidden' }}>
        <Text size='medium' weight='bold' css={[dsStyles.truncate, { lineHeight: '16px' }]}>
          {title}
        </Text>
        <Text size='small' color='light' css={dsStyles.truncate}>
          {duration}
        </Text>
      </div>
    </div>
  );
};

type AppointmentCardSwitcherProps = Pick<
  EventCardComponentProps,
  'overlappingIndicatorVariant' | 'isYesterdayEvent'
> & {
  duration: string;
  appointmentDetails: ExtendedAppointment;
  eventId: string;
};

const AppointmentCardSwitcher = (props: AppointmentCardSwitcherProps) => {
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');

  return lastUsedVertical === 'VET' ? <VetEventCard {...props} /> : <EventCard {...props} />;
};

const VetEventCard = ({
  duration,
  appointmentDetails,
  eventId,
  isYesterdayEvent,
  overlappingIndicatorVariant,
}: AppointmentCardSwitcherProps) => {
  const appointmentInfo = useMemo(() => {
    const appointmentInfoToPrepare = appointmentDetails ? [appointmentDetails] : [];
    return prepareAppointmentInfo(appointmentInfoToPrepare ?? [], eventId);
  }, [appointmentDetails, eventId]);

  const { data: personData } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: appointmentInfo?.personInfo?.id ?? '',
      locationIds: [appointmentInfo?.locationId ?? ''],
    },
    {
      enabled: !!appointmentInfo?.personInfo?.id,
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );

  const personId = appointmentInfo?.personInfo?.id || '';
  const serviceableIds = appointmentInfo?.petInfo?.serviceableIds || [];

  const petQuery = useGetActivePetsQuery({
    personId: personId || '',
    locationId: appointmentInfo?.locationId || '',
  });
  const pet = petQuery.data?.filter((pet) => pet.petServiceableId === serviceableIds?.[0])[0];

  if (!appointmentInfo) {
    return null;
  }

  const overlappingIndicator = (variant: OverlappingEventIndicatorVariantType) => (
    <OverlappingEventIndicator
      variant={variant}
      isYesterdayEvent={isYesterdayEvent}
      visible={overlappingIndicatorVariant === variant}
    />
  );

  return (
    <div>
      <div css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Text as='span' size='medium' weight='bold' css={{ marginRight: theme.spacing(0.5) }}>
          {pet?.name ?? ''}
        </Text>
        <Text as='span' size='medium'>
          {personData?.LastName ?? ''}
        </Text>
        {overlappingIndicator('dot')}
      </div>
      <MotionText size='small' color='light'>
        {duration}
        {overlappingIndicator('icon')}
      </MotionText>
      {overlappingIndicator('full')}
    </div>
  );
};

const EventCard = ({ eventId, appointmentDetails, ...rest }: AppointmentCardSwitcherProps) => {
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarEvents');
  const { selectedGroupId } = useAppScopeStore();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const { isDental, locationIdWiseEIVCustomizationFlagDetails } = useHasWIVOrEIVAccess();
  const { appointmentStatus, setAppointmentStatus } = useAppointmentStatusShallowStore(
    'appointmentStatus',
    'setAppointmentStatus'
  );
  const [selectedStatus, setSelectedStatus] = useState<EventStatus | undefined>();
  const appointmentInfo = useMemo(() => {
    const appointmentInfoToPrepare = appointmentDetails ? [appointmentDetails] : [];
    return prepareAppointmentInfo(appointmentInfoToPrepare ?? [], eventId);
  }, [appointmentDetails, eventId]);

  const { id: personId, sourceId, PMSId } = appointmentInfo?.personInfo || {};
  const { locationId = '' } = appointmentInfo || {};

  const isNonAttendedV3Event = isScheduleV3FlagEnabled && checkIsNonAttendedEventStatus(selectedStatus);

  const { isIntegratedOffice, isLoading: isLoadingIntegrationDetails } =
    DataSourcesQueries.useGetAccessibleIntegrations();
  const updateApptStatusMutation = AppointmentStatusHooks.useUpdateAppointmentStatusV3(locationId);
  const writebackApptStatusMutation = SchedulerV3.Mutations.useAppointmentStatusWriteBackMutation();

  const handleWriteback = async (selectedApptStatus: AppointmentStatusTypes.AppointmentStatusType) => {
    const { key, value, label, icon, variant } = selectedApptStatus;
    const weaveStatus = WeaveAppointmentStatus[key as keyof typeof AppointmentStatusV3Enum];

    const payload = {
      locationId: locationId,
      sourceTenantId: appointmentDetails?.sourceTenantId ?? '',
      appointmentExternalId: appointmentDetails?.externalId ?? '',
      ...(selectedGroupId && { parentLocationId: selectedGroupId }),
      ...(value && { externalStatusId: value }),
      ...(weaveStatus && { weaveStatus }),
    };

    try {
      await writebackApptStatusMutation.mutateAsync(payload);
      setAppointmentStatus({
        key,
        value,
        text: label,
        iconName: icon,
        variant,
        id: appointmentDetails?.id ?? '',
        updatedAt: formatDate(dayjs()),
      });
      alert.success(t('Appointment status updated. It might take a few minutes to reflect'));
    } catch (error) {
      alert.error(t('Error updating appointment status'));
    }
  };

  const handleStatusChange = (status?: AppointmentStatusTypes.AppointmentStatusType) => {
    if (!status) return;

    if (isIntegratedOffice) {
      handleWriteback(status);
    } else {
      const existingStartDate = formatDate(appointmentDetails.start, DATETIME_FORMATS.ISO_DATE);
      const existingReferenceId = appointmentDetails.referenceId || '';
      updateApptStatusMutation.handleUpdateStatus({
        eventId,
        status: status.key,
        existingStartDate,
        existingReferenceId,
      });
    }
    setSelectedStatus(status.key);
  };

  useEffect(() => {
    const status = appointmentInfo?.appointmentDetails?.status;
    if (!!status && selectedStatus !== status) {
      setSelectedStatus(status as EventStatus);
    }
  }, [appointmentInfo?.appointmentDetails?.status]);

  useEffect(() => {
    if (isIntegratedOffice) {
      const apptStatusInStore = appointmentStatus.find((item) => item.id === appointmentDetails.id);
      if (apptStatusInStore) {
        setSelectedStatus(apptStatusInStore.key as EventStatus);
      }
    }
  }, [appointmentDetails.id, appointmentStatus, isIntegratedOffice, selectedStatus]);

  const isEnhancedEligibilityEnabled = useMemo(() => {
    // TODO: Remove isDental check when EIV/WIV is enabled for all the specialties
    return isDental && locationIdWiseEIVCustomizationFlagDetails[locationId || ''];
  }, [isDental, locationId, locationIdWiseEIVCustomizationFlagDetails]);

  const renderStatusChip = () => {
    const statusChipProps = {
      status: selectedStatus,
      isIconOnly: true,
      bordered: true,
      darkModeMenu: true,
      disabled: isLoadingIntegrationDetails,
      isLoading: updateApptStatusMutation.isLoading || writebackApptStatusMutation.isLoading,
      css: { height: 16, width: 20 },
      wrapperStyle: [statusChipsWrapperStyle],
    };

    return (
      <>
        {locationId &&
          appointmentDetails.id &&
          (isScheduleV3FlagEnabled ? (
            <AppointmentStatusComponents.EditableStatusChip
              {...statusChipProps}
              appointmentDetails={appointmentDetails}
              disabled={statusChipProps.disabled}
              isIntegrated={isIntegratedOffice}
              onStatusChange={handleStatusChange}
            />
          ) : (
            <AppointmentStatusV2 {...statusChipProps} locationId={locationId} appointmentId={appointmentDetails.id} />
          ))}
      </>
    );
  };

  const renderInsuranceStatusChip = () => {
    return (
      <InsuranceTypeProvider>
        <ScheduleAppointmentCardInsuranceStatusChip
          locationId={locationId || ''}
          personId={personId || ''}
          sourceId={sourceId || ''}
          pmsID={PMSId || ''}
          calendarDate={calendarDate}
        />
      </InsuranceTypeProvider>
    );
  };

  return (
    <AppointmentEventCardPreview
      {...rest}
      name={appointmentInfo?.personInfo.name || ''}
      isNonAttendedV3Event={isNonAttendedV3Event}
      renderStatusChip={renderStatusChip}
      renderInsuranceStatusChip={isEnhancedEligibilityEnabled ? renderInsuranceStatusChip : undefined}
    />
  );
};

const statusChipsWrapperStyle = css({
  '&:hover > .chip': {
    outline: `2px solid ${theme.colors.primary50}`,
    outlineOffset: 1,
  },
});
